import { getAccessorySlotData } from '@components/AlternativesOverlay/actions/getAccessorySlotData';
import AlternativeProductOverlayContent from '@components/AlternativesOverlay/AlternativeProductOverlayContent';
import ContainedLoader from '@components/ContainedLoader/ContainedLoader';
import { Subsidiary } from '@coolblue-development/constants';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Flex,
  freddyTheme,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Typography,
  useMediaQuery
} from '@coolblue-development/freddy';
import closeOverlayTrigger from '@coolblue-development/ga4-events/triggers/CrossSellAlternatives/closeOverlayTrigger';
import { default as productTypeSelectChooseAlternativeTrigger } from '@coolblue-development/ga4-events/triggers/ProductTypeSection/selectChooseAlternativeTrigger';
import { default as productTypeShownAlternativesTrigger } from '@coolblue-development/ga4-events/triggers/ProductTypeSection/shownAlternativesTrigger';
import { default as recommendedSelectChooseAlternativeTrigger } from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/selectChooseAlternativeTrigger';
import { default as recommendedShownAlternativesTrigger } from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/shownAlternativesTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { AddToCartButton } from '@coolblue-development/next-shopping-cart/client';
import { useAccessoryProductCardContext } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import { useCustomerContext } from '@features/add-to-cart-page/components/Sections/CustomerContext/CustomerContext';
import { AccessorySlot } from '@features/add-to-cart-page/typings/Product';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { useState } from 'react';

export default function ProductAlternativeOverlay({
  productTypeNameSingular,
  productTypeNamePlural,
  productTypeId,
  classificationId,
  mainProductId,
  accessoryProductCardIndex,
  accessoryProductCardTotalIndices,
  ctaRef,
  isAutomated,
  quantity
}: {
  productTypeNameSingular?: string;
  productTypeNamePlural?: string;
  productTypeId: number;
  classificationId?: string;
  mainProductId: number;
  accessoryProductCardIndex: number;
  accessoryProductCardTotalIndices: number;
  ctaRef: RefObject<HTMLInputElement | null>;
  isAutomated: boolean;
  quantity: ReactNode;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [accessorySlot, setAccessorySlot] = useState<AccessorySlot | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ctaIsLoading, setCtaIsLoading] = useState<boolean>(false);

  const { t } = useTranslation('webshop');
  const { subsidiary } = useRequestContext();
  const testId = useTestId('alternatives-overlay-link');
  const modalTranslations = useFreddyTranslations('modal');
  const drawerTranslations = useFreddyTranslations('drawer');
  const { trackEvent } = useAnalytics();
  const context = useAccessoryProductCardContext();
  const isMediumBreakpointOrUp = useMediaQuery(freddyTheme.breakpoint.minWidth('md'), true);
  const { isBusinessCustomer } = useCustomerContext();

  const handleOpen = async () => {
    setIsLoading(true);
    setOpen(true);

    if (!accessorySlot) {
      const accessorySlot = await getAccessorySlotData(
        mainProductId,
        productTypeId,
        classificationId
      );
      context.updateProductAlternatives({ accessorySlot: accessorySlot ?? undefined });
      setAccessorySlot(accessorySlot);
      const accessorySlotLength = accessorySlot?.products.length ?? 0;
      trackSelectChooseAlternative(accessorySlotLength);
      const attachedIds = assembleAttachedAlternativeProductIds(accessorySlot);
      renderTrackShownAlternatives(isAutomated, attachedIds);
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    trackEvent(
      closeOverlayTrigger({
        sub_feature: 'add_to_cart_page_overlay'
      })
    );
  };

  const onAlternativeSelected = (value: string) => {
    context.setSelectedProductAlternative(+value);
  };

  const trackSelectChooseAlternative = (accessorySlotLength: number) => {
    if (!productTypeNameSingular) return;
    const params = {
      subFeature: isBusinessCustomer ? ('business_atc_page' as const) : null,
      index: accessoryProductCardIndex,
      total_indices: accessoryProductCardTotalIndices,
      item_list_id: productTypeId,
      item_list_name: productTypeNameSingular as string,
      number_of_items: accessorySlotLength
    };

    if (isAutomated) {
      trackEvent(recommendedSelectChooseAlternativeTrigger(params));
    } else {
      trackEvent(productTypeSelectChooseAlternativeTrigger(params));
    }
  };
  const assembleAttachedAlternativeProductIds = (
    accessorySlot?: AccessorySlot | null
  ): number[] => {
    const attachedItemIds: number[] = [];
    accessorySlot?.products?.forEach(slotProduct => {
      attachedItemIds.push(slotProduct.product.id);
    });
    accessorySlot?.highlightedAlternative?.slots.forEach(slotProduct => {
      attachedItemIds.push(slotProduct.products[0].product.id);
    });

    return attachedItemIds;
  };
  const renderTrackShownAlternatives = (isAutomated: boolean, attachedIds: number[]) => {
    const params = {
      primary_item_id: mainProductId,
      attached_item_ids: attachedIds.join(',')
    };

    if (isAutomated) {
      trackEvent(recommendedShownAlternativesTrigger(params));
    } else {
      trackEvent(productTypeShownAlternativesTrigger(params));
    }
  };

  const Footer = (): ReactElement => {
    const addToCartProductId = context.selectedProductAlternative;

    const handleOnClick = (): void => {
      if (!addToCartProductId) return;
      setCtaIsLoading(true);
      ctaRef.current?.click();
      handleClose();
      setCtaIsLoading(false);
    };

    const addToCartButton = (
      <AddToCartButton
        label={t('addToCart.addToCartLabel')}
        ariaLabel={t('addToCart.addToCartLabel')}
        isFullWidth={!isMediumBreakpointOrUp}
        disabled={!addToCartProductId}
        loading={ctaIsLoading}
        onClick={() => handleOnClick()}
      />
    );

    return isBusinessCustomer ? (
      <Flex
        flex={1}
        alignItems={{ md: 'center' }}
        justifyContent='space-between'
        direction={{
          xs: 'column',
          md: 'row'
        }}
        gap={{ xs: 200, md: 0 }}
      >
        <Flex alignItems='center' gap={200}>
          <Typography variant='h3' mb={0}>
            {t('addToCart.quantity')}
          </Typography>
          {quantity}
        </Flex>
        {addToCartButton}
      </Flex>
    ) : (
      addToCartButton
    );
  };

  const body = accessorySlot ? (
    <SelectableBoxGroup
      inputPlacement='floating'
      name='alternativesOverlay'
      spacing='default'
      type='radio'
      onChange={value => {
        onAlternativeSelected(value);
      }}
      value={context.selectedProductAlternative.toString()}
    >
      <AlternativeProductOverlayContent
        accessorySlot={accessorySlot}
        productTypeNamePlural={productTypeNamePlural}
        isMediumBreakpointOrUp={isMediumBreakpointOrUp}
        mainProductId={mainProductId}
        accessoryProductCardIndex={accessoryProductCardIndex}
        accessoryProductCardTotalIndices={accessoryProductCardTotalIndices}
        isAutomated={isAutomated}
      />
    </SelectableBoxGroup>
  ) : (
    <>
      <Typography>{t('alternativesOverlay.errorMessage')}</Typography>
    </>
  );

  return (
    <Box mt={300} {...testId}>
      <Link onClick={handleOpen}>
        {subsidiary === Subsidiary.DE
          ? t('alternativesOverlay.openOverlayLink')
          : t('alternativesOverlay.otherOptions')}
      </Link>
      {isMediumBreakpointOrUp && (
        <Modal
          open={open}
          onClose={handleClose}
          title={t('alternativesOverlay.chooseYour', { productTypeNameSingular })}
          size='xlarge'
          aria-label={t('alternativesOverlay.chooseYour', { productTypeNameSingular })}
          translations={modalTranslations}
          body={isLoading ? <ContainedLoader height={650} /> : <ModalBody>{body}</ModalBody>}
          footer={
            <ModalFooter>
              <Footer />
            </ModalFooter>
          }
        />
      )}
      {!isMediumBreakpointOrUp && (
        <Drawer
          open={open}
          onClose={handleClose}
          placement='left'
          aria-label={t('alternativesOverlay.chooseYour', { productTypeNameSingular })}
          translations={drawerTranslations}
          header={
            <DrawerHeader
              title={t('alternativesOverlay.chooseYour', { productTypeNameSingular })}
            />
          }
          body={isLoading ? <ContainedLoader height={650} /> : <DrawerBody>{body}</DrawerBody>}
          footer={
            <DrawerFooter>
              <Footer />
            </DrawerFooter>
          }
        />
      )}
    </Box>
  );
}
